import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { Edit3, Save, RotateCcw, AlertTriangle, Calculator, MapPin, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback markets (used if API fails)
const FALLBACK_MARKETS = [
  { id: 'jacksonville', name: 'Jacksonville', slug: 'jacksonville' },
  { id: 'orlando', name: 'Orlando', slug: 'orlando' },
  { id: 'tampa', name: 'Tampa', slug: 'tampa' },
  { id: 'west-palm', name: 'West Palm', slug: 'west-palm' },
  { id: 'naples', name: 'Naples/Ft. Myers', slug: 'naples' },
];

const METRIC_FIELDS = [
  { key: 'leads', label: 'New Opportunities', type: 'number' },
  { key: 'appointments_set', label: 'Appointments Set', type: 'number' },
  { key: 'leads_issued', label: 'Leads Issued', type: 'number' },
  { key: 'demos', label: 'Demos', type: 'number' },
  { key: 'jobs_sold', label: 'Jobs Sold', type: 'number' },
  { key: 'payments_received', label: 'Payments Received', type: 'number' },
  { key: 'sold_jobs_amount', label: 'Sold Jobs Amount', type: 'currency' },
  { key: 'revenue_amount', label: 'Revenue Amount', type: 'currency' },
];

export function MetricsTrueUpModal({ open, onOpenChange, currentMetrics, period, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [activeMarket, setActiveMarket] = useState('all');
  const [marketOverrides, setMarketOverrides] = useState({});
  const [totals, setTotals] = useState({});
  const [markets, setMarkets] = useState([]);

  // Fetch markets from API on open
  useEffect(() => {
    if (open) {
      fetchMarkets();
    }
  }, [open]);

  // Load existing overrides after markets are loaded
  useEffect(() => {
    if (open && markets.length > 0) {
      fetchAllMarketOverrides();
    }
  }, [open, period, markets]);

  // Recalculate totals when market overrides change
  useEffect(() => {
    calculateTotals();
  }, [marketOverrides, markets]);

  const fetchMarkets = async () => {
    setLoadingMarkets(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/api/marketing/pacing/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const apiMarkets = response.data?.markets || [];
      if (apiMarkets.length > 0) {
        // Convert API markets to modal format using slug as id
        const formattedMarkets = apiMarkets.map(m => ({
          id: m.slug,
          name: m.name,
          slug: m.slug
        }));
        setMarkets(formattedMarkets);
      } else {
        setMarkets(FALLBACK_MARKETS);
      }
    } catch (error) {
      console.error('Failed to fetch markets:', error);
      setMarkets(FALLBACK_MARKETS);
    } finally {
      setLoadingMarkets(false);
    }
  };

  const fetchAllMarketOverrides = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const overridesData = {};
      
      // Fetch overrides for each market
      for (const market of markets) {
        const params = new URLSearchParams({ period: period || 'mtd', market: market.slug });
        try {
          const response = await axios.get(`${API}/api/marketing/pacing/true-up?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data?.overrides) {
            overridesData[market.slug] = response.data.overrides;
          }
        } catch (e) {
          // Market might not have overrides yet
        }
      }
      
      setMarketOverrides(overridesData);
    } catch (error) {
      console.error('Failed to fetch overrides:', error);
    }
  };

  const calculateTotals = () => {
    const newTotals = {};
    
    METRIC_FIELDS.forEach(field => {
      let total = 0;
      markets.forEach(market => {
        const marketData = marketOverrides[market.slug] || {};
        const value = parseFloat(marketData[field.key]) || 0;
        total += value;
      });
      newTotals[field.key] = total;
    });
    
    setTotals(newTotals);
  };

  const handleMarketValueChange = (marketSlug, fieldKey, value) => {
    setMarketOverrides(prev => ({
      ...prev,
      [marketSlug]: {
        ...(prev[marketSlug] || {}),
        [fieldKey]: value
      }
    }));
  };

  const handleSaveMarket = async (marketSlug) => {
    if (marketSlug === 'all') {
      // Save all markets
      await handleSaveAll();
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const marketData = marketOverrides[marketSlug] || {};
      
      const payload = {
        period: period || 'mtd',
        market: marketSlug
      };
      
      // Add non-empty values
      METRIC_FIELDS.forEach(field => {
        const value = marketData[field.key];
        if (value !== undefined && value !== '' && value !== null) {
          payload[field.key] = field.type === 'currency' 
            ? parseFloat(value) 
            : parseInt(value);
        }
      });
      
      await axios.post(`${API}/api/marketing/pacing/true-up`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const marketName = markets.find(m => m.slug === marketSlug)?.name || marketSlug;
      toast.success(`${marketName} true-up saved`);
    } catch (error) {
      console.error('Failed to save true-up:', error);
      toast.error('Failed to save true-up');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Save each market that has data
      for (const market of markets) {
        const marketData = marketOverrides[market.slug];
        if (!marketData || Object.keys(marketData).length === 0) continue;
        
        const payload = {
          period: period || 'mtd',
          market: market.slug
        };
        
        METRIC_FIELDS.forEach(field => {
          const value = marketData[field.key];
          if (value !== undefined && value !== '' && value !== null) {
            payload[field.key] = field.type === 'currency' 
              ? parseFloat(value) 
              : parseInt(value);
          }
        });
        
        await axios.post(`${API}/api/marketing/pacing/true-up`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Also save the totals as the "all" market override (null market = all markets)
      const totalsPayload = {
        period: period || 'mtd',
        market: null // null means all markets combined
      };
      
      METRIC_FIELDS.forEach(field => {
        if (totals[field.key] > 0) {
          totalsPayload[field.key] = totals[field.key];
        }
      });
      
      await axios.post(`${API}/api/marketing/pacing/true-up`, totalsPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('All markets saved successfully');
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save all markets:', error);
      toast.error('Failed to save markets');
    } finally {
      setLoading(false);
    }
  };

  const handleClearMarket = async (marketSlug) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      if (marketSlug === 'all') {
        // Clear all markets
        for (const market of markets) {
          await axios.post(`${API}/api/marketing/pacing/true-up`, {
            period: period || 'mtd',
            market: market.slug,
            clear_override: true
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        // Clear totals
        await axios.post(`${API}/api/marketing/pacing/true-up`, {
          period: period || 'mtd',
          market: null,
          clear_override: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMarketOverrides({});
        toast.success('All overrides cleared');
      } else {
        await axios.post(`${API}/api/marketing/pacing/true-up`, {
          period: period || 'mtd',
          market: marketSlug,
          clear_override: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMarketOverrides(prev => {
          const newOverrides = { ...prev };
          delete newOverrides[marketSlug];
          return newOverrides;
        });
        
        const marketName = markets.find(m => m.slug === marketSlug)?.name || marketSlug;
        toast.success(`${marketName} overrides cleared`);
      }
      
      onSaved?.();
    } catch (error) {
      console.error('Failed to clear overrides:', error);
      toast.error('Failed to clear overrides');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value, type) => {
    if (value === undefined || value === null || value === '') return '';
    if (type === 'currency') {
      return parseFloat(value).toLocaleString();
    }
    return value.toString();
  };

  const getMarketData = (marketSlug) => {
    if (marketSlug === 'all') {
      return totals;
    }
    return marketOverrides[marketSlug] || {};
  };

  const hasMarketData = (marketSlug) => {
    if (marketSlug === 'all') {
      return Object.values(totals).some(v => v > 0);
    }
    const data = marketOverrides[marketSlug];
    return data && Object.keys(data).length > 0;
  };

  // Build tabs list with "All Markets" first
  const allTabs = [
    { id: 'all', slug: 'all', name: 'All Markets' },
    ...markets
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-blue-500" />
            True-Up Metrics by Market
          </DialogTitle>
          <DialogDescription>
            Enter values from Builder Prime for each market. Totals are calculated automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Period Info */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Period:</strong> {period?.toUpperCase() || 'MTD'}
            </p>
          </div>

          {loadingMarkets ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            /* Market Tabs */
            <Tabs value={activeMarket} onValueChange={setActiveMarket}>
              <TabsList className="grid mb-4" style={{ gridTemplateColumns: `repeat(${Math.min(allTabs.length, 6)}, 1fr)` }}>
                {allTabs.map(market => (
                  <TabsTrigger 
                    key={market.slug} 
                    value={market.slug}
                    className="text-xs relative"
                    data-testid={`tab-${market.slug}`}
                  >
                    <span className="truncate">{market.slug === 'all' ? 'Total' : market.name.split('/')[0]}</span>
                    {hasMarketData(market.slug) && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              {allTabs.map(market => (
                <TabsContent key={market.slug} value={market.slug} className="space-y-4">
                  {/* Market Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {market.name}
                      {hasMarketData(market.slug) && (
                        <Badge variant="outline" className="text-amber-600 border-amber-600">
                          Has Overrides
                        </Badge>
                      )}
                    </h3>
                    {market.slug === 'all' && (
                      <Badge className="bg-blue-500 gap-1">
                        <Calculator className="h-3 w-3" />
                        Auto-calculated from markets
                      </Badge>
                    )}
                  </div>

                  {/* Volume Metrics */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Volume Metrics
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      {METRIC_FIELDS.slice(0, 6).map(field => (
                        <div key={field.key} className="space-y-1">
                          <Label className="text-xs">{field.label}</Label>
                          <Input
                            type="number"
                            placeholder={market.slug === 'all' ? 'Total' : `Enter ${field.label}`}
                            value={getMarketData(market.slug)[field.key] || ''}
                            onChange={(e) => handleMarketValueChange(market.slug, field.key, e.target.value)}
                            disabled={market.slug === 'all'}
                            className={market.slug === 'all' ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : ''}
                            data-testid={`input-${market.slug}-${field.key}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Revenue Metrics */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Revenue Metrics
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {METRIC_FIELDS.slice(6).map(field => (
                        <div key={field.key} className="space-y-1">
                          <Label className="text-xs">{field.label}</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder={market.slug === 'all' ? 'Total' : `Enter amount`}
                              value={getMarketData(market.slug)[field.key] || ''}
                              onChange={(e) => handleMarketValueChange(market.slug, field.key, e.target.value)}
                              disabled={market.slug === 'all'}
                              className={`pl-7 ${market.slug === 'all' ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : ''}`}
                              data-testid={`input-${market.slug}-${field.key}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Market Actions */}
                  <div className="flex justify-between pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClearMarket(market.slug)}
                      disabled={loading || !hasMarketData(market.slug)}
                      className="gap-2"
                      data-testid={`btn-clear-${market.slug}`}
                    >
                      <RotateCcw className="h-4 w-4" />
                      {market.slug === 'all' ? 'Clear All' : 'Clear'}
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => handleSaveMarket(market.slug)}
                      disabled={loading}
                      className="gap-2"
                      data-testid={`btn-save-${market.slug}`}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {loading ? 'Saving...' : market.slug === 'all' ? 'Save All Markets' : 'Save Market'}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveAll} disabled={loading || loadingMarkets} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {loading ? 'Saving...' : 'Save All & Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MetricsTrueUpModal;
